import React, { useState, useRef } from "react";
import Footer from "../common/Footer";
import Navigation from "../common/Navigation";
import Sidebar from "../common/Sidebar";
import CommonService from "../../services/CommonService";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FormInput from "../common/FormInput";

const Pan = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [panDetails, setPanDetails] = useState(null);
  const [aadhaarLinked, setAadhaarLinked] = useState("NA");
  const formRef = useRef(null);

  //  Validation Schema
  const validationSchema = Yup.object().shape({
    pan: Yup.string()
      .required("PAN number is required")
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format"),
    name: Yup.string()
      .required("Name is required")
      .matches(/^[A-Z\s]+$/, "Only uppercase letters and spaces allowed"),
  });

  //  React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
  });

  //  Reset handler
  const handleCancel = () => {
    reset();
    setPanDetails(null);
    setAadhaarLinked("NA");
  };

  //  Submit handler (no payload variable)
  const onSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const response = await CommonService.panSearchAdvance({
        pan_number: formData.pan.toUpperCase(),
        name: formData.name,
      });

      if (response.data.status === 200) {
        setPanDetails(response.data);
        setAadhaarLinked(
          response.data.data.aadhaar_linked ? "True" : "NA"
        );
      } else {
        Swal.fire({
          icon: "error",
          title: response.data.status,
          text: response.data.message,
        });
        setPanDetails(null);
      }
    } catch (error) {
      console.error("Error:", error?.response?.data || error);
      const err = error?.response?.data;
      Swal.fire({
        icon: "error",
        title: err?.status || "Error",
        text: err?.message || "Something went wrong",
      });
      setPanDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Sidebar />
          <div className="layout-page">
            <Navigation />
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <div className="row">
                  <div className="col-md-12">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="row">
                          <h5 className="card-header">PAN Advance Report</h5>
                          <hr className="hr-line" />
                        </div>

                        {/* Loader */}
                        {isLoading ? (
                          <div className="d-flex justify-content-center">
                            <div className="sk-fold sk-primary">
                              <div className="sk-fold-cube"></div>
                              <div className="sk-fold-cube"></div>
                              <div className="sk-fold-cube"></div>
                              <div className="sk-fold-cube"></div>
                            </div>
                          </div>
                        ) : (
                          //  Form
                          <form
                            className="card-body"
                            ref={formRef}
                            style={{ paddingTop: "0px", paddingLeft: "0px" }}
                            onSubmit={handleSubmit(onSubmit)}
                          >
                            <div className="row">
                              <div className="col-md-4">
                                <FormInput
                                  label="pan"
                                  name="pan"
                                  register={register}
                                  errors={errors}
                                  placeholder="Enter Your Pan"
                                  maxLength={100}
                                  onInput={(e) =>
                                    (e.target.value = e.target.value.toUpperCase())
                                  }
                                />
                              </div>

                              <div className="col-md-4">
                                <FormInput
                                  label="Name"
                                  name="name"
                                  register={register}
                                  errors={errors}
                                  placeholder="Enter Your Name"
                                  maxLength={100}
                                  onInput={(e) =>
                                    (e.target.value = e.target.value.toUpperCase())
                                  }
                                />
                              </div>
                            </div>

                            <div className="pt-4">
                              <button
                                type="submit"
                                className="btn btn-primary me-sm-3 me-1"
                              >
                                Find Status
                              </button>
                              <button
                                type="button"
                                className="btn btn-label-secondary"
                                onClick={handleCancel}
                              >
                                Reset
                              </button>
                            </div>
                          </form>
                        )}

                        {/*  PAN Details Display */}
                        {panDetails && (
                          <div className="row">
                            {/* Left Section */}
                            <div className="col-md-7">
                              <ul className="nav nav-pills flex-column flex-sm-row mb-4">
                                <p
                                  className="nav-link active"
                                  style={{ fontSize: "19px" }}
                                >
                                  <i className="ti-xs ti ti-user-check me-1"></i>{" "}
                                  PAN Details
                                </p>
                              </ul>
                              <div className="card-body" style={{ paddingTop: 0 }}>
                                {[
                                  ["PAN Number", panDetails.data.data.pan],
                                  [
                                    "Pan Holder Title",
                                    panDetails.data.data.registered_name,
                                  ],
                                  [
                                    "Name",
                                    panDetails.data.data.name_pan_card,
                                  ],
                                  [
                                    "Date of Birth",
                                    panDetails.data.data.date_of_birth,
                                  ],
                                  [
                                    "Name Provided",
                                    panDetails.data.data.name_provided,
                                  ],
                                  [
                                    "Mobile",
                                    panDetails.data.data.mobile_number,
                                  ],
                                  ["Gender", panDetails.data.data.gender],
                                  ["Aadhaar Seeding Status", aadhaarLinked],
                                  [
                                    "Aadhaar Seeding No.",
                                    panDetails.data.data.masked_aadhaar_number,
                                      panDetails.data.data.masked_aadhaar_number ? "text-success" : "",
                                  ],
                                  ["Email", panDetails.data.data.email],
                                  [
                                    "Pan Status",
                                    panDetails.data.data.status,
                                    panDetails.data.data.status === "VALID"
                                      ? "text-success"
                                      : "text-danger",
                                  ],
                                  [
                                    "Address",
                                    panDetails.data.data.address?.full_address,
                                  ],
                                  ["Type", panDetails.data.data.type],
                                ].map(([label, value, extraClass], idx) => (
                                  <div
                                    key={idx}
                                    className="row"
                                    style={{
                                      background:
                                        idx % 2 === 0 ? "#ebe9e9" : "transparent",
                                    }}
                                  >
                                    <div className="col-md-6">
                                      <b>{label}</b>
                                    </div>
                                    <div
                                      className={`col-md-6 text-end ${
                                        extraClass || ""
                                      }`}
                                    >
                                      {value || "NA"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Right Section (API Health) */}
                            <div className="col-md-5">
                              <ul className="nav nav-pills flex-column flex-sm-row mb-4">
                                <p
                                  className="nav-link active"
                                  style={{ fontSize: "19px" }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="icon icon-tabler icons-tabler-outline icon-tabler-clock-12"
                                  >
                                    <path
                                      stroke="none"
                                      d="M0 0h24v24H0z"
                                      fill="none"
                                    />
                                    <path d="M3 12a9 9 0 0 0 9 9m9 -9a9 9 0 1 0 -18 0" />
                                    <path d="M12 7v5l.5 .5" />
                                    <path d="M18 15h2a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1h-1a1 1 0 0 0 -1 1v1a1 1 0 0 0 1 1h2" />
                                    <path d="M15 21v-6" />
                                  </svg>{" "}
                                  &nbsp; API HEALTH CHECK
                                </p>
                              </ul>
                              <div className="card-body" style={{ paddingTop: 0 }}>
                                {[
                                  ["Request Time", panDetails.data.requestTimestamp],
                                  ["Response Time", panDetails.data.responseTimestamp],
                                  ["Latency(sec)", panDetails.data.totalTime],
                                ].map(([label, value], idx) => (
                                  <div
                                    key={idx}
                                    className="row"
                                    style={{
                                      background:
                                        idx % 2 === 0 ? "#ebe9e9" : "transparent",
                                    }}
                                  >
                                    <div className="col-md-6">
                                      <b>{label}</b>
                                    </div>
                                    <div className="col-md-6 text-end">
                                      {value}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Footer />
              <div className="content-backdrop fade"></div>
            </div>
          </div>
        </div>
        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>
    </>
  );
};

export default Pan;